/**
 * Client for `POST /api/core/exec` (docs/superpowers/specs/2026-10-21-core-headless-routing-design.md): runs a task as a supervised
 * core run. There is no single-loop sandbox fallback: a route that is off for this user (403/404) throws.
 */

export interface ExecOpts {
  baseUrl: string;
  token: string;
  repo: string;
  prompt: string;
  branch?: string;
  ghToken?: string;
  signal?: AbortSignal;
  fetchFn?: typeof fetch;
}

export type CoreExecResult = { success: boolean; logs: string; diff: string; exitCode: number; runId?: string; state?: string };

export async function execViaCore(opts: ExecOpts): Promise<CoreExecResult> {
  const fetchFn = opts.fetchFn ?? fetch;
  const payload: Record<string, unknown> = { repo: opts.repo, prompt: opts.prompt };
  if (opts.branch !== undefined) payload.branch = opts.branch;
  if (opts.ghToken !== undefined) payload.ghToken = opts.ghToken;

  const res = await fetchFn(`${opts.baseUrl.replace(/\/+$/, '')}/api/core/exec`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${opts.token}`, 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    signal: opts.signal,
  });

  if (res.status === 403 || res.status === 404) {
    throw new Error(`Core exec is not available for this account (${res.status}): ${(await res.text()).slice(0, 300)}`);
  }
  if (res.status !== 200) {
    const text = (await res.text()).slice(0, 300);
    const retry = res.status === 429 ? res.headers.get('Retry-After') : null;
    throw new Error(`Core exec failed (${res.status}): ${text}${retry ? ` retry after ${retry}s` : ''}`);
  }

  const body = (await res.json()) as unknown;
  if (typeof body !== 'object' || body === null) throw new Error('Core exec response is not an object');
  const o = body as Record<string, unknown>;
  // The route sends its headers at once and keeps the body alive, so a failure after that arrives as `{ error, status }` in a 200.
  if (typeof o.error === 'string' && o.success === undefined) {
    const status = typeof o.status === 'number' ? o.status : 500;
    const retry = typeof o.retryAfterSeconds === 'number' ? ` retry after ${o.retryAfterSeconds}s` : '';
    throw new Error(`Core exec failed (${status}): ${o.error.slice(0, 300)}${retry}`);
  }
  const field = <T>(name: string, type: 'boolean' | 'string' | 'number'): T => {
    if (typeof o[name] !== type) throw new Error(`Core exec response field ${name} is missing or not a ${type}`);
    return o[name] as T;
  };
  return {
    success: field<boolean>('success', 'boolean'),
    logs: field<string>('logs', 'string'),
    diff: field<string>('diff', 'string'),
    exitCode: field<number>('exitCode', 'number'),
    ...(typeof o.runId === 'string' ? { runId: o.runId } : {}),
    ...(typeof o.state === 'string' ? { state: o.state } : {}),
  };
}
